import { AlertTriangle, CheckCircle2, Circle } from "lucide-react";

import { StatusPill } from "@/components/status-pill";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { type AdminAccountPermissions, useMcpConnectionInfo } from "@/lib/api";
import { cn } from "@/lib/utils";

import { CodeBlock } from "./code-block";

interface Props {
  account: AdminAccountPermissions;
}

/**
 * Everything a customer needs to connect an agent to this account's MCP server:
 * a readiness checklist, the endpoint URL, and per-client setup instructions.
 */
export function ConnectionKit({ account }: Props) {
  const connectionQuery = useMcpConnectionInfo();
  const info = connectionQuery.data;

  // MCP sign-in is federated to Microsoft/Entra, so an active contact just needs
  // an email — that's the identity they sign in with; there's no password to set.
  const hasSignInReadyContact = account.contacts.some((c) => c.active && !!c.email);

  return (
    <section className="space-y-3">
      <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
        Connection kit
      </h3>
      <div className="bg-card border-border space-y-6 rounded-lg border p-5 shadow-sm">
        {connectionQuery.isLoading ? (
          <div className="space-y-6">
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded-full" />
                  <Skeleton className="h-4 w-64 max-w-full" />
                  <Skeleton className="ml-auto h-5 w-16" />
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-9 w-full rounded-md" />
            </div>
          </div>
        ) : connectionQuery.isError || !info ? (
          <p className="text-muted text-sm">
            Could not load connection details.{" "}
            <button
              type="button"
              onClick={() => void connectionQuery.refetch()}
              className="underline"
            >
              try again
            </button>
          </p>
        ) : (
          <>
            <div className="space-y-2">
              <ReadinessItem ok={info.configured} label="MCP server configured" />
              <ReadinessItem ok={account.mcp_enabled} label="MCP access enabled for this account" />
              <ReadinessItem
                ok={hasSignInReadyContact}
                label="At least one active contact has an email"
              />
            </div>

            {info.configured ? (
              <>
                <div className="space-y-2">
                  <p className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
                    MCP endpoint
                  </p>
                  <CodeBlock value={info.mcp_url} label="Copy MCP URL" />
                </div>

                <Tabs defaultValue="copilot" className="gap-4">
                  <TabsList variant="line" className="flex-wrap">
                    <TabsTrigger value="copilot">Microsoft Copilot Studio</TabsTrigger>
                    <TabsTrigger value="generic">Any MCP client</TabsTrigger>
                  </TabsList>

                  <TabsContent value="copilot" className="space-y-5">
                    <div className="space-y-3">
                      <p className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
                        1 — Add the MCP server to your agent
                      </p>
                      <Steps
                        steps={[
                          "In your agent, open the Tools tab.",
                          "Choose Add a tool → New tool → Model Context Protocol.",
                          "Enter the server name and description — use the values below.",
                          <>
                            Set Server URL to{" "}
                            <code className="text-foreground bg-warm-gray-02 rounded px-1 font-mono text-xs">
                              {info.mcp_url}
                            </code>
                            .
                          </>,
                          "For Authentication, choose OAuth 2.0 → Dynamic discovery.",
                          <>
                            Set the authentication mode to <strong>User</strong> — every person
                            using the agent signs in with their own work email, so their own J&J
                            permissions apply.
                          </>,
                          "Select Create, then Add to agent.",
                        ]}
                      />
                      <div className="space-y-2">
                        <p className="text-muted text-xs">Server name (copy-paste):</p>
                        <CodeBlock value="JnJ MedTech Supply Chain" label="Copy server name" />
                        <p className="text-muted text-xs">Description (copy-paste):</p>
                        <CodeBlock
                          value="Johnson & Johnson MedTech supply-chain data for your account: check order status and tracking, search the product catalog, view list pricing, and place or approve orders. Each user signs in with their own work email; access follows the permissions J&J set for them."
                          label="Copy description"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <p className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
                        2 — Publish and make it available in Copilot &amp; Teams
                      </p>
                      <Steps
                        steps={[
                          "Select Publish (top right) so the agent version with the J&J tools goes live.",
                          "Open Channels and enable Teams and Microsoft 365 Copilot.",
                          "Choose availability: share the link with specific people or a security group, or submit for admin approval to make it appear for everyone in the Teams and M365 Copilot app stores.",
                          "Org-wide availability is approved by your Microsoft 365 admin (Teams admin center → Manage apps).",
                          "On first use, each colleague is asked to sign in with their own work email before the J&J tools respond.",
                        ]}
                      />
                    </div>

                    <p className="text-muted text-xs">
                      Requires a Copilot Studio license with Copilot Credits; the callback URI
                      Copilot Studio shows during setup is registered automatically.
                    </p>
                  </TabsContent>

                  <TabsContent value="generic" className="space-y-3">
                    <div className="space-y-2">
                      <FieldRow label="MCP URL" value={info.mcp_url} />
                      <FieldRow
                        label="Authorization server metadata"
                        value={info.authorization_server_metadata_url}
                      />
                      <FieldRow label="Resource metadata" value={info.resource_metadata_url} />
                    </div>
                    {info.scopes.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-muted text-xs">Scopes:</span>
                        {info.scopes.map((scope: string) => (
                          <StatusPill key={scope}>{scope}</StatusPill>
                        ))}
                      </div>
                    )}
                    <p className="text-muted text-xs">
                      OAuth 2.1 authorization-code + PKCE, dynamic client registration, streamable
                      HTTP.
                    </p>
                  </TabsContent>
                </Tabs>
              </>
            ) : (
              <div className="bg-jj-orange-03/10 border-jj-orange-03/30 flex items-start gap-2 rounded-md border p-3">
                <AlertTriangle className="text-jj-orange-03 mt-0.5 h-4 w-4 shrink-0" />
                <p className="text-foreground text-sm">
                  The MCP server isn&apos;t configured yet. Once an operator sets the public base
                  URL and OAuth endpoints, connection instructions will appear here.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function ReadinessItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle2 className="text-jj-green-03 h-4 w-4 shrink-0" />
      ) : (
        <Circle className="text-jj-orange-03 h-4 w-4 shrink-0" />
      )}
      <span className={cn("text-sm", ok ? "text-foreground" : "text-muted")}>{label}</span>
      <StatusPill tone={ok ? "green" : "orange"} className="ml-auto">
        {ok ? "Ready" : "Pending"}
      </StatusPill>
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">{label}</p>
      <CodeBlock value={value} label={`Copy ${label}`} />
    </div>
  );
}

function Steps({ steps }: { steps: React.ReactNode[] }) {
  return (
    <ol className="space-y-2">
      {steps.map((step, i) => (
        <li key={i} className="flex gap-3 text-sm">
          <span className="bg-warm-gray-02 text-foreground flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-medium">
            {i + 1}
          </span>
          <span className="text-foreground leading-relaxed">{step}</span>
        </li>
      ))}
    </ol>
  );
}
